/**
 * ReAuthDialog — "prove who you are" before unmasking Privacy mode.
 *
 * Auth method ladder (highest user-friendliness first):
 *   1. Passkey / Biometric  — Face ID, Touch ID, Windows Hello, Android
 *                             fingerprint via WebAuthn. Shown when the user
 *                             has at least one registered credential on
 *                             this account. Tapping triggers the platform's
 *                             native biometric sheet; no PIN entry required.
 *   2. PIN                  — 4–6 digit numeric, shown if the user has set
 *                             one (queried at mount via /api/auth/privacy-status).
 *                             Mobile gets numeric keypad via inputMode="numeric".
 *   3. Password             — fallback when neither passkey nor PIN exists.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Fingerprint } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useAuth } from '../contexts/AuthContext';
import { authenticateWithPasskey, isWebAuthnSupported } from '../utils/webauthnClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReAuthDialog = () => {
  const { reAuthOpen, onReAuthSuccess, onReAuthCancel } = usePrivacy();
  const { user } = useAuth();
  // `has_pin` / `has_passkey` are baked into the auth payload at login &
  // /auth/me so the dialog can pick the right input INSTANTLY — no
  // separate /auth/privacy-status round-trip (used to add 6-7s latency
  // on flaky links).
  const hasPin = !!user?.has_pin;
  // Local override: if a biometric attempt reveals there are no usable
  // credentials on this device, suppress the passkey UI for the rest of
  // this dialog session.
  const [passkeyUnavailable, setPasskeyUnavailable] = useState(false);
  const hasPasskey = !!user?.has_passkey && isWebAuthnSupported() && !passkeyUnavailable;
  const [mode, setMode] = useState(() => {
    if (user?.has_passkey && isWebAuthnSupported()) return 'passkey';
    if (user?.has_pin) return 'pin';
    return 'password';
  });
  const [value, setValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const passkeyAttemptedRef = useRef(false);

  // ── Reset transient state + pick the right starting mode on open ──────
  useEffect(() => {
    if (!reAuthOpen) {
      setValue('');
      setError('');
      passkeyAttemptedRef.current = false;
      return;
    }
    if (hasPasskey) setMode('passkey');
    else if (hasPin) setMode('pin');
    else setMode('password');
    setTimeout(() => {
      if (!hasPasskey) inputRef.current?.focus();
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reAuthOpen]);

  // ── Biometric unlock — must be called from a user-event handler ──────
  const tryPasskey = useCallback(async () => {
    setError('');
    setBioBusy(true);
    passkeyAttemptedRef.current = true;
    try {
      const ok = await authenticateWithPasskey();
      if (ok) {
        onReAuthSuccess();
      } else {
        setError('Biometric verification failed. Please try again or use your PIN.');
      }
    } catch (e2) {
      const name = e2?.name || '';
      if (name === 'NotAllowedError') {
        // User cancelled or timed out — silently swap to PIN/password.
        if (hasPin) setMode('pin');
        else setMode('password');
        setError('');
      } else if (e2?.code === 'NO_CREDENTIALS') {
        setPasskeyUnavailable(true);
        if (hasPin) setMode('pin');
        else setMode('password');
      } else {
        setError(name === 'InvalidStateError'
          ? 'This device is not registered for biometric unlock.'
          : 'Biometric is unavailable — please use your PIN.');
        if (hasPin) setMode('pin');
        else setMode('password');
      }
    } finally {
      setBioBusy(false);
    }
  }, [hasPin, onReAuthSuccess]);

  // ── PIN / password submit ────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!value) {
      setError(mode === 'pin' ? 'Enter your PIN' : 'Enter your password');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'pin') {
        await axios.post(`${API_URL}/api/auth/pin/verify`, { pin: value }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/auth/verify-password`, { password: value }, { withCredentials: true });
      }
      onReAuthSuccess();
    } catch (e2) {
      setError(e2?.response?.data?.detail || (mode === 'pin' ? 'Incorrect PIN' : 'Incorrect password'));
    } finally {
      setBusy(false);
    }
  };

  const showBiometricStage = hasPasskey && mode === 'passkey';

  return (
    <Dialog open={reAuthOpen} onOpenChange={(o) => !o && onReAuthCancel()}>
      <DialogContent className="sm:max-w-sm" data-testid="reauth-dialog">
        <DialogHeader>
          <DialogTitle>Verify it&apos;s you</DialogTitle>
          <DialogDescription>
            {showBiometricStage
              ? 'Use Face ID / Touch ID / Fingerprint to reveal your investment figures.'
              : mode === 'pin'
                ? 'Enter your Privacy PIN to reveal your investment figures.'
                : 'Enter your account password to reveal your investment figures.'}
          </DialogDescription>
        </DialogHeader>

        {showBiometricStage ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={tryPasskey}
              disabled={bioBusy}
              data-testid="reauth-passkey-btn"
              className="w-full flex items-center justify-center gap-2 rounded-md border py-3 transition-colors hover:bg-stone-50 disabled:opacity-60"
              style={{ borderColor: '#1B4332', color: '#1B4332', backgroundColor: '#F7FAF8' }}
            >
              {bioBusy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Fingerprint className="h-5 w-5" />
              )}
              <span className="font-medium">
                {bioBusy ? 'Waiting for biometric…' : 'Unlock with Biometrics'}
              </span>
            </button>
            {error && (
              <p className="text-xs" style={{ color: '#9A3412' }} data-testid="reauth-error">{error}</p>
            )}
            <div className="flex items-center justify-center gap-3 pt-1">
              {hasPin && (
                <button
                  type="button"
                  onClick={() => { setMode('pin'); setError(''); setTimeout(() => inputRef.current?.focus(), 30); }}
                  className="text-[11px] underline"
                  style={{ color: '#57534E' }}
                  data-testid="reauth-use-pin"
                >
                  Use PIN instead
                </button>
              )}
              <button
                type="button"
                onClick={() => { setMode('password'); setError(''); setTimeout(() => inputRef.current?.focus(), 30); }}
                className="text-[11px] underline"
                style={{ color: '#57534E' }}
                data-testid="reauth-use-password"
              >
                Use password
              </button>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={onReAuthCancel} disabled={bioBusy}>
                Cancel
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="reauth-input" className="text-xs">
                {mode === 'pin' ? 'Privacy PIN' : 'Account password'}
              </Label>
              <div className="relative">
                <Input
                  ref={inputRef}
                  id="reauth-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  inputMode={mode === 'pin' ? 'numeric' : 'text'}
                  pattern={mode === 'pin' ? '[0-9]*' : undefined}
                  maxLength={mode === 'pin' ? 6 : 200}
                  autoComplete={mode === 'pin' ? 'one-time-code' : 'current-password'}
                  placeholder={mode === 'pin' ? '••••' : '••••••••'}
                  data-testid="reauth-input"
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                  style={{ color: '#78716C' }}
                  data-testid="reauth-toggle-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <div className="flex items-center justify-between mt-1">
                {hasPin && (
                  <button
                    type="button"
                    onClick={() => { setMode(mode === 'pin' ? 'password' : 'pin'); setValue(''); setError(''); }}
                    className="text-[11px] underline"
                    style={{ color: '#1B4332' }}
                    data-testid="reauth-switch-method"
                  >
                    {mode === 'pin' ? 'Use account password instead' : 'Use Privacy PIN instead'}
                  </button>
                )}
                {hasPasskey && (
                  <button
                    type="button"
                    onClick={() => { setMode('passkey'); setError(''); }}
                    className="text-[11px] underline ml-auto"
                    style={{ color: '#1B4332' }}
                    data-testid="reauth-back-to-passkey"
                  >
                    Use biometric
                  </button>
                )}
              </div>
            </div>
            {error && (
              <p className="text-xs" style={{ color: '#9A3412' }} data-testid="reauth-error">{error}</p>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={onReAuthCancel} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="reauth-submit"
                style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {busy ? 'Verifying…' : 'Reveal'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReAuthDialog;
